/**
 * League market profile — descriptive statistics over a league's own auction
 * history (see fetchLeagueAuctionHistory in sleeperImport.js).
 *
 * This is a READBACK, not an input. Nothing here feeds pricing, tiers, or the
 * AI; it exists to show a user the shape of their own market. Keep it that way
 * — the moment these numbers drive bidding they become a calibration surface
 * and need real validation behind them.
 *
 * Returns raw numbers (fractions, not percentages, unrounded dollars). All
 * formatting decisions belong to the caller.
 */

// Below this many priced non-keeper picks the shares are noise — a single
// $70 buy can swing a position share several points. Better to show nothing
// than a confident-looking number built on one thin draft.
const MIN_PICKS = 100;

// "Top 12" is the first-round-caliber band: roughly one premium buy per team
// in the leagues this targets. Computed PER DRAFT and then averaged, never
// over the pooled set — pooling three seasons and taking the global top 12
// would report the twelve biggest prices ever paid, which is a maximum, not
// a market average.
const TOP_N = 12;

// Sleeper reports $1 and $2 buys as the bottom of the market; their share is
// the cleanest single read on how top-heavy a room is.
const CHEAP_MAX = 2;

/**
 * @param {Array} history — [{ season, draftId, picks: [{ name, pos, amount, keeper }] }]
 * @returns {Object|null} profile, or null when there isn't enough data
 */
export function computeLeagueMarketProfile(history) {
  if (!Array.isArray(history) || history.length === 0) return null;

  // Keeper prices are carried contracts, not market clearing prices — a $25
  // Ja'Marr Chase keeper would drag every statistic here toward fiction.
  // Keepers are still COUNTED (keeperShare below), because how much of a
  // league is pre-committed is itself a fact about that league's market.
  const drafts = [];
  let pricedTotal = 0;
  let keeperTotal = 0;
  for (const season of history) {
    const priced = (season?.picks || []).filter(
      p => Number.isFinite(p.amount) && p.amount > 0
    );
    pricedTotal += priced.length;
    keeperTotal += priced.filter(p => p.keeper).length;
    const picks = priced.filter(p => !p.keeper);
    if (picks.length) drafts.push({ season: season.season, picks });
  }
  if (!drafts.length) return null;

  const pooled = drafts.flatMap(d => d.picks);
  if (pooled.length < MIN_PICKS) return null;

  const totalSpend = pooled.reduce((sum, p) => sum + p.amount, 0);
  if (totalSpend <= 0) return null;

  // Position shares use the FULL denominator, so DEF/K spend is counted even
  // though callers typically render only the skill positions. The displayed
  // shares therefore sum to slightly under 100% — that is honest, not a bug.
  const spendByPos = new Map();
  for (const p of pooled) {
    const pos = p.pos || "—";
    spendByPos.set(pos, (spendByPos.get(pos) || 0) + p.amount);
  }
  const positionShares = [...spendByPos.entries()]
    .map(([pos, spend]) => ({ pos, spend, share: spend / totalSpend }))
    .sort((a, b) => b.share - a.share);

  // Per-draft top band, then averaged across drafts. A draft with fewer than
  // TOP_N priced picks contributes its own average rather than being dropped,
  // so a thin season still counts once.
  const perDraftTopAvgs = drafts.map(d => {
    const top = [...d.picks].sort((a, b) => b.amount - a.amount).slice(0, TOP_N);
    return top.reduce((sum, p) => sum + p.amount, 0) / top.length;
  });
  const topAvg = perDraftTopAvgs.reduce((a, b) => a + b, 0) / perDraftTopAvgs.length;

  const cheapCount = pooled.filter(p => p.amount <= CHEAP_MAX).length;

  return {
    nYears: drafts.length,
    nPicks: pooled.length,
    totalSpend,
    positionShares,
    topAvg,
    topN: TOP_N,
    cheapShare: cheapCount / pooled.length,
    cheapMax: CHEAP_MAX,
    // Share of all priced picks that were keepers. Denominator includes
    // keepers; a league where half the board is pre-committed reports 0.5.
    keeperShare: pricedTotal > 0 ? keeperTotal / pricedTotal : 0,
    // The raw count behind that share. Surfaced because "59 kept players
    // excluded" tells a user what was left out of these numbers in a way a
    // percentage doesn't.
    keeperCount: keeperTotal,
  };
}

// Reference spend shares the multipliers are measured against. These mirror
// SMITHTOWN_BENCHMARKS.posSpend in src/tools/batchSim.js — the project's
// calibration baseline. Duplicated rather than imported because batchSim is a
// Node-side tool and importing it would drag the whole batch sim into the
// browser bundle. HAND-SYNC PACT: if the Smithtown baseline moves, move this
// with it, or calibrated leagues quietly drift against the engine they tune.
export const REFERENCE_SPEND_SHARES = { QB: 0.12, RB: 0.38, WR: 0.40, TE: 0.07 };

// A league can only pull a position's prices this far from the reference.
// Real leagues produce ratios well outside this (Thunderdome's QB share is
// 0.64x reference); the clamp is what keeps one weird position from
// rewriting the board.
const MULT_MIN = 0.75;
const MULT_MAX = 1.35;

// Years of history needed before a league's own market is trusted at the full
// clamped strength. Three seasons buys 0.75 — deliberately never 1.0, since
// even a long history is one league's sample.
const FULL_TRUST_YEARS = 4;

// Share of picks that go for $1-2 in the baseline room: 77 of 160 picks, from
// SMITHTOWN_BENCHMARKS.priceBuckets in batchSim.js. Same hand-sync pact as
// REFERENCE_SPEND_SHARES. This is the yardstick for "top-heavy" — note it is
// HIGH (48%), so a league at 40% is flatter than baseline, not more top-heavy.
const CHEAP_SHARE_REFERENCE = 77 / 160;

// A position within this many points of its reference share is unremarkable,
// and a scouting report that lists the unremarkable is noise. Room shape gets
// a wider band — it's one number over the whole board and moves more.
const SHARE_TOLERANCE = 0.03;
const CHEAP_TOLERANCE = 0.05;
const MAX_CLAUSES = 3;

const POS_LABEL = { QB: "QB", RB: "RBs", WR: "WRs", TE: "TEs" };

/**
 * Plain-language read on what makes a league's market distinctive — the
 * conclusions, not the arithmetic. Percentages appear only where the number
 * IS the point (room shape); positions get verdicts.
 *
 * Clauses are lowercase and unpunctuated so a caller can join them with a
 * separator and capitalize the first. Returns [] for a profile-less league,
 * and a single "typical" clause when nothing stands out — a scout who finds
 * nothing notable says so rather than handing back a blank page.
 *
 * @returns {string[]} up to MAX_CLAUSES clauses
 */
export function describeMarket(profile) {
  if (!profile) return [];

  const observed = new Map(profile.positionShares.map(p => [p.pos, p.share]));
  const posClauses = [];
  for (const [pos, reference] of Object.entries(REFERENCE_SPEND_SHARES)) {
    const share = observed.get(pos);
    if (share == null) continue;
    const delta = share - reference;
    if (Math.abs(delta) < SHARE_TOLERANCE) continue;
    posClauses.push({
      mag: Math.abs(delta),
      text: delta > 0
        ? `pays up for ${POS_LABEL[pos]}`
        : `goes cheap at ${POS_LABEL[pos]}`,
    });
  }
  // Loudest deviations first — a report leads with what's most unusual.
  posClauses.sort((a, b) => b.mag - a.mag);

  const cheapPct = Math.round(profile.cheapShare * 100);
  const cheapDelta = profile.cheapShare - CHEAP_SHARE_REFERENCE;
  let roomClause = null;
  if (cheapDelta >= CHEAP_TOLERANCE) {
    roomClause = `top-heavy room (${cheapPct}% of buys at $1-${profile.cheapMax})`;
  } else if (cheapDelta <= -CHEAP_TOLERANCE) {
    roomClause = `money spread around (only ${cheapPct}% of buys at $1-${profile.cheapMax})`;
  }

  // Reserve the last slot for room shape when it fires — it's a different
  // dimension from the position reads and shouldn't be crowded out by them.
  const clauses = posClauses
    .slice(0, roomClause ? MAX_CLAUSES - 1 : MAX_CLAUSES)
    .map(c => c.text);
  if (roomClause) clauses.push(roomClause);

  return clauses.length ? clauses : ["spends like a fairly standard room"];
}

/**
 * Per-position price multipliers derived from a league's own market.
 *
 * observed/reference share, clamped, then shrunk toward 1.0 by a confidence
 * weight. Keeper-heavy leagues shrink harder: when a third of the board never
 * hits the open market, the remaining prices describe that league's auction
 * less faithfully, so they earn less influence.
 *
 * Only the four skill positions are returned. DEF/K sit at $1-2 where a
 * multiplier is rounding noise, and their shares are too small to measure.
 *
 * @returns {Object|null} { QB, RB, WR, TE } multipliers, or null
 */
export function buildLeaguePriceMultipliers(profile) {
  if (!profile) return null;

  const observed = new Map(profile.positionShares.map(p => [p.pos, p.share]));
  const weight =
    (Math.min(profile.nYears, FULL_TRUST_YEARS - 1) / FULL_TRUST_YEARS) *
    (1 - (profile.keeperShare || 0));

  const mults = {};
  for (const [pos, reference] of Object.entries(REFERENCE_SPEND_SHARES)) {
    const share = observed.get(pos);
    // A position with no observed spend is missing data, not a $0 market —
    // leave it alone rather than clamping it to the 0.75 floor.
    if (!share || reference <= 0) { mults[pos] = 1; continue; }
    const clamped = Math.min(MULT_MAX, Math.max(MULT_MIN, share / reference));
    mults[pos] = 1 + (clamped - 1) * weight;
  }
  return mults;
}
